import { Building2, SlidersHorizontal, Users } from 'lucide-react'
import { useState } from 'react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet'
import {
  useAsyncProviders,
  useAsyncManagers,
  useAsyncUsers,
} from '../hooks/useAsyncSearch'
import { useShipmentNotificationFilters } from './hooks/useShipmentNotificationFilters'
import AsyncSelect from '../components/AsyncSelect'
import { useDebounce } from '@/hooks/useDebounce'

export const AdvancedFilters = () => {
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false)

  const { searchProviders } = useAsyncProviders()
  const { searchManagers } = useAsyncManagers()
  const { searchUsers } = useAsyncUsers()

  const {
    filters,
    updateProviderCompanyId,
    updateManagerCompanyId,
    updateCreatedById,
    clearAllFilters,
  } = useShipmentNotificationFilters()

  // Local state for staged filter values
  const [localProvider, setLocalProvider] = useState(
    filters.providerCompanyId?.[0] ? parseInt(filters.providerCompanyId[0]) : 0
  )
  const [localManager, setLocalManager] = useState(
    filters.managerCompanyId?.[0] ? parseInt(filters.managerCompanyId[0]) : 0
  )
  const [localCreatedBy, setLocalCreatedBy] = useState(
    filters.createdById?.[0] ? parseInt(filters.createdById[0]) : 0
  )

  // Estados locales para búsqueda y debounce
  const [providerSearch, setProviderSearch] = useState('')
  const [managerSearch, setManagerSearch] = useState('')
  const [userSearch, setUserSearch] = useState('')
  const debouncedProviderSearch = useDebounce(providerSearch, 500)
  const debouncedManagerSearch = useDebounce(managerSearch, 500)
  const debouncedUserSearch = useDebounce(userSearch, 500)

  // Sync local state when opening drawer
  const handleOpenChange = (open: boolean) => {
    setIsFilterDrawerOpen(open)
    if (open) {
      setLocalProvider(
        filters.providerCompanyId?.[0]
          ? parseInt(filters.providerCompanyId[0])
          : 0
      )
      setLocalManager(
        filters.managerCompanyId?.[0]
          ? parseInt(filters.managerCompanyId[0])
          : 0
      )
      setLocalCreatedBy(
        filters.createdById?.[0] ? parseInt(filters.createdById[0]) : 0
      )
    }
  }

  const handleApplyFilters = () => {
    updateProviderCompanyId(
      localProvider ? [localProvider.toString()] : ['all']
    )
    updateManagerCompanyId(localManager ? [localManager.toString()] : ['all'])
    updateCreatedById(localCreatedBy ? [localCreatedBy.toString()] : ['all'])
    setIsFilterDrawerOpen(false)
  }

  const handleClearAllFilters = () => {
    clearAllFilters()
    setIsFilterDrawerOpen(false)
  }

  // Count active advanced filters
  const getActiveAdvancedFiltersCount = () => {
    let count = 0
    if (
      filters.providerCompanyId &&
      filters.providerCompanyId.length > 0 &&
      !filters.providerCompanyId.includes('all')
    )
      count++
    if (
      filters.managerCompanyId &&
      filters.managerCompanyId.length > 0 &&
      !filters.managerCompanyId.includes('all')
    )
      count++
    if (
      filters.createdById &&
      filters.createdById.length > 0 &&
      !filters.createdById.includes('all')
    )
      count++
    return count
  }

  return (
    <Sheet onOpenChange={handleOpenChange} open={isFilterDrawerOpen}>
      <SheetTrigger asChild>
        <Button
          aria-label="Filtros avanzados"
          className="gap-1.5"
          variant="outline"
        >
          <SlidersHorizontal className="h-4 w-4" />
          <span className="hidden sm:inline">Filtros avanzados</span>
          {getActiveAdvancedFiltersCount() > 0 && (
            <Badge className="ml-1 rounded-full px-1.5" variant="secondary">
              {getActiveAdvancedFiltersCount()}
            </Badge>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>Filtros avanzados</SheetTitle>
          <SheetDescription>
            Configura filtros específicos para encontrar exactamente lo que
            buscas.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 py-6">
            {/* Proveedor */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Building2 className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-medium">Proveedor</h3>
              </div>
              <div className="space-y-2">
                <Label htmlFor="provider">Seleccionar proveedor</Label>
                <AsyncSelect
                  debouncedSearchQuery={debouncedProviderSearch}
                  disabled={false}
                  error=""
                  label=""
                  onChange={setLocalProvider}
                  onSearch={searchProviders}
                  placeholder="Seleccione un proveedor"
                  required={false}
                  searchQuery={providerSearch}
                  setSearchQuery={setProviderSearch}
                  value={localProvider}
                />
              </div>
            </div>

            <Separator />

            {/* Operador Logístico */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Building2 className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-medium">Operador Logístico</h3>
              </div>
              <div className="space-y-2">
                <Label htmlFor="manager">Seleccionar operador</Label>
                <AsyncSelect
                  debouncedSearchQuery={debouncedManagerSearch}
                  disabled={false}
                  error=""
                  label=""
                  onChange={setLocalManager}
                  onSearch={searchManagers}
                  placeholder="Seleccione un operador logístico"
                  required={false}
                  searchQuery={managerSearch}
                  setSearchQuery={setManagerSearch}
                  value={localManager}
                />
              </div>
            </div>

            <Separator />

            {/* Creado por */}
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Users className="h-4 w-4 text-gray-500" />
                <h3 className="text-sm font-medium">Creado por</h3>
              </div>
              <div className="space-y-2">
                <Label htmlFor="createdBy">Seleccionar usuario</Label>
                <AsyncSelect
                  debouncedSearchQuery={debouncedUserSearch}
                  disabled={false}
                  error=""
                  label=""
                  onChange={setLocalCreatedBy}
                  onSearch={searchUsers}
                  placeholder="Seleccione un usuario"
                  required={false}
                  searchQuery={userSearch}
                  setSearchQuery={setUserSearch}
                  value={localCreatedBy}
                />
              </div>
            </div>
          </div>
        </ScrollArea>

        <SheetFooter className="flex-row justify-between gap-2 sm:justify-between">
          <Button
            className="flex-1"
            onClick={handleClearAllFilters}
            variant="outline"
          >
            Limpiar
          </Button>
          <Button className="flex-1" onClick={handleApplyFilters}>
            Aplicar
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  )
}
